import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
} from "@mui/material";
import { HostSystemItemSortType } from "@tlc/ls2pac/dto/HostSystemItemSortType";
import { useLanguage } from "../../Language";
import * as React from "react";

type HostSystemItemType = "hold" | "loan" | "history";

const HostSystemItemSortOptions: Record<
  HostSystemItemType,
  HostSystemItemSortType[]
> = {
  hold: [
    HostSystemItemSortType.Status,
    HostSystemItemSortType.QueuePosition,
    HostSystemItemSortType.Title,
    HostSystemItemSortType.Author,
  ],
  loan: [
    HostSystemItemSortType.Status,
    HostSystemItemSortType.Title,
    HostSystemItemSortType.Author,
  ],
  history: [
    HostSystemItemSortType.OutDate,
    HostSystemItemSortType.Title,
    HostSystemItemSortType.Author,
  ],
};

interface HostSystemItemSortControlProps
  extends SelectProps<HostSystemItemSortType> {
  type: HostSystemItemType;
}

const HostSystemItemSortControl: React.FC<HostSystemItemSortControlProps> = ({
  type,
  ...props
}) => {
  const { translate } = useLanguage();
  const labels: Record<HostSystemItemSortType, string> = {
    [HostSystemItemSortType.OutDate]: translate("checkoutDate"),
    [HostSystemItemSortType.DueDate]: translate("exportData_dueDate"),
    [HostSystemItemSortType.Author]: translate("author"),
    [HostSystemItemSortType.QueuePosition]: translate("queuePosition"),
    [HostSystemItemSortType.Status]: translate("status"),
    [HostSystemItemSortType.Title]: translate("title"),
  };
  const options = HostSystemItemSortOptions[type];
  const labelId = `${type}-sort-label`;
  return (
    <FormControl>
      <InputLabel id={labelId}>{translate("sortBy")}</InputLabel>
      <Select
        size="small"
        labelId={labelId}
        label={translate("sortBy")}
        {...props}
      >
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {labels[option]}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const makeSortTypeGuard =
  (type: HostSystemItemType) =>
  (param: string): param is HostSystemItemSortType =>
    HostSystemItemSortOptions[type].includes(param as HostSystemItemSortType);

export const isHoldsSortType = makeSortTypeGuard("hold");
export const isLoansSortType = makeSortTypeGuard("loan");
export const isLoanHistorySortType = makeSortTypeGuard("history");
export const HoldsSortType = typeof HostSystemItemSortOptions.hold;
export const LoansSortType = HostSystemItemSortOptions.loan;
export const LoanHistorySortType = HostSystemItemSortOptions.history;

export const HoldsSortControl = (
  props: Omit<HostSystemItemSortControlProps, "type">
) => {
  return <HostSystemItemSortControl {...props} type="hold" />;
};

export const LoansSortControl = (
  props: Omit<HostSystemItemSortControlProps, "type">
) => {
  return <HostSystemItemSortControl {...props} type="loan" />;
};

export const LoanHistorySortControl = (
  props: Omit<HostSystemItemSortControlProps, "type">
) => {
  return <HostSystemItemSortControl {...props} type="history" />;
};
